import { useQueries, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { getEvaluatedPermissionsCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import _isEqual from 'lodash/isEqual';

const intl = getIntl('UseEvaluatedPermissions', {
    errorLoadingPermissions: 'Error occurred loading permissions',
});

export type UseEvaluatedPermissionsArgs = {
    medicalProfileId: string | null;
};

export type UseEvaluatedPermissionsAcrossProfilesArgs = {
    medicalProfileIds: string[];
};

export type UpdateContactDetailsArgs = {
    medicalProfileId: string;
};
export type UseEvaluatedPermissionsReturnValue = {
    permissions: APITypes.medicalProfileService.EvaluatedProfilePermissionsResponse;
    isLoading: boolean;
};

export type UseEvaluatedPermissionsAcrossProfilesReturnValue = {
    permissions: Record<string, APITypes.medicalProfileService.EvaluatedProfilePermissionsResponse>;
    isLoading: boolean;
};

const fullyRestrictedPermissions: APITypes.medicalProfileService.EvaluatedProfilePermissionsResponse = {
    isProfileOwner: false,
    permissions: {
        idsAndInfo: 'denied',
        medications: 'denied',
        conditions: 'denied',
        allergies: 'denied',
        careTeam: 'denied',
        visits: 'denied',
        patientPortals: 'denied',
        cloudCalendars: 'denied',
        settings: 'denied',
        reports: 'denied',
        files: 'denied',
        imaging: 'denied',
        procedures: 'denied',
        vaccinations: 'denied',
        labs: 'denied',
        collaborators: 'denied',
    },
};

export default function useEvaluatedPermissions({
    medicalProfileId,
}: UseEvaluatedPermissionsArgs): UseEvaluatedPermissionsReturnValue {
    const permissionsQuery = useQuery({
        queryKey: getEvaluatedPermissionsCacheKey(medicalProfileId || ''),
        queryFn: async () => {
            return await appAPI.get(urls.api.permissions.evaluatedPermissions(medicalProfileId!));
        },
        initialData: fullyRestrictedPermissions,
        enabled: !!medicalProfileId,
    });
    useQueryErrorNotify(permissionsQuery, intl('errorLoadingPermissions'));

    const permissions = permissionsQuery.data ?? fullyRestrictedPermissions;
    return {
        permissions,
        // is loading is always false if initial data is provided.  Is fetching is true if the query is in flight
        isLoading: _isEqual(permissions, fullyRestrictedPermissions)
            ? permissionsQuery.isFetching
            : permissionsQuery.isLoading,
    };
}

export const useEvaluatedPermissionsAcrossProfiles = ({
    medicalProfileIds,
}: UseEvaluatedPermissionsAcrossProfilesArgs): UseEvaluatedPermissionsAcrossProfilesReturnValue => {
    const permissionsQueries = useQueries({
        queries: medicalProfileIds.map((medicalProfileId) => ({
            queryKey: getEvaluatedPermissionsCacheKey(medicalProfileId),
            queryFn: async () => {
                return await appAPI.get(urls.api.permissions.evaluatedPermissions(medicalProfileId));
            },
            initialData: fullyRestrictedPermissions,
        })),
    });

    return {
        permissions: permissionsQueries.reduce((acc, query, index) => {
            acc[medicalProfileIds[index]] = query.data ?? fullyRestrictedPermissions;
            return acc;
        }, {} as Record<string, APITypes.medicalProfileService.EvaluatedProfilePermissionsResponse>),
        isLoading: permissionsQueries.some((query) => query.isLoading),
    };
};
